// ============================================================================
// SdpDebugPanel.tsx
// Panneau debug SDP + utilitaires SDP partagés
//
// Exports publics :
//   - colorizeSdpLine(line)          → React.ReactElement (<span> coloré)
//   - getSdpLineColor(line)          → string (couleur CSS seule)
//   - SdpAccordion                   → accordéon SDP réutilisable
//   - SdpDebugButton                 → bouton flottant 🔍
//   - SdpDebugPanel                  → panneau fullscreen debug SDP
//
// Usage régie :
//   <SdpDebugPanel mode="regie" log={sdpLogRegie} onClose={…} onClear={…} initialDevice="all" />
//
// Usage device :
//   <SdpDebugPanel mode="device" log={sdpLog} onClose={…} onClear={…} />
// ============================================================================

import React from 'react';
import { SdpLogEntry }               from '../../../dataTypes/liveStreamManagment/types_sdp';
import { profileLabel } from '../../../dataTypes/liveStreamManagment/utils_codec';
import { getDeviceColor } from '../../../dataTypes/liveStreamManagment/utils_device';

// ─── Coloration syntaxique SDP ────────────────────────────────────────────────

export const getSdpLineColor = (line: string): string => {
  if (/profile-level-id/.test(line))                          return '#fde047';
  if (/a=rtpmap|a=fmtp/.test(line))                          return '#34d399';
  if (/^a=candidate|^a=ice/.test(line))                      return '#1e3a2a';
  if (/^m=/.test(line))                                       return '#60a5fa';
  if (/^o=|^s=/.test(line))                                   return '#94a3b8';
  return '#2a5a3a';
};

export const colorizeSdpLine = (line: string, index: number): React.ReactElement => (
  <span
    key={index}
    style={{
      color: getSdpLineColor(line),
      display: 'block',
      lineHeight: 1.4,
      fontWeight: /profile-level-id/.test(line) ? 700 : 'normal',
    }}
  >
    {line || '\u00a0'}
  </span>
);

// ─── SdpAccordion ─────────────────────────────────────────────────────────────

export interface SdpAccordionProps {
  title: string;
  sdp?:  string;
}

export const SdpAccordion: React.FC<SdpAccordionProps> = ({ title, sdp }) => (
  <details style={{ marginBottom: 8 }}>
    <summary style={{
      cursor: 'pointer', fontSize: 11, color: 'rgba(255,255,255,0.4)',
      padding: '5px 0', userSelect: 'none', listStyle: 'none',
    }}>
      ▸ {title}
    </summary>
    {sdp ? (
      <div style={{
        background: '#030803', borderRadius: 6, border: '1px solid #0a1f0a',
        padding: '10px 12px', maxHeight: '40vh', overflowY: 'auto', marginTop: 6,
      }}>
        {sdp.split(/\r?\n/).map((line, i) => colorizeSdpLine(line, i))}
      </div>
    ) : (
      <p style={{ margin: '6px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.2)', fontStyle: 'italic' }}>
        Aucun SDP disponible
      </p>
    )}
  </details>
);

// ─── SdpDebugButton ───────────────────────────────────────────────────────────

export interface SdpDebugButtonProps {
  count:    number;
  hasError: boolean;
  onClick:  () => void;
  bottom?: number;
  right?:  number;
  zIndex?: number;
}

export const SdpDebugButton: React.FC<SdpDebugButtonProps> = ({
  count, hasError, onClick, bottom = 24, right = 24, zIndex = 200,
}) => {
  const color = hasError ? '#dc2626' : count > 0 ? '#16a34a' : '#334155';
  return (
    <button
      onClick={onClick}
      title={`SDP Debug (${count} entrée${count !== 1 ? 's' : ''}${hasError ? ' — ERREUR' : ''})`}
      style={{
        position: 'fixed', bottom, right, zIndex,
        width: 52, height: 52, borderRadius: '50%',
        background: color, color: 'white', border: 'none',
        fontSize: 20, cursor: 'pointer',
        boxShadow: `0 4px 16px rgba(0,0,0,0.5), 0 0 0 3px ${
          hasError ? 'rgba(220,38,38,0.3)' : count > 0 ? 'rgba(22,163,74,0.3)' : 'rgba(51,65,85,0.3)'
        }`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      🔍
      {count > 0 && (
        <span style={{
          position: 'absolute', top: -4, right: -4,
          width: 18, height: 18, borderRadius: '50%',
          background: hasError ? '#ef4444' : '#fff',
          color: hasError ? '#fff' : color,
          fontSize: 9, fontWeight: 700, fontFamily: 'monospace',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `2px solid ${color}`,
        }}>
          {count > 9 ? '9+' : count}
        </span>
      )}
    </button>
  );
};

// ─── Props SdpDebugPanel ──────────────────────────────────────────────────────

interface SdpDebugPanelBaseProps {
  log:     SdpLogEntry[];
  onClose: () => void;
  onClear: () => void;
}

interface SdpDebugPanelRegieProps extends SdpDebugPanelBaseProps {
  mode:           'regie';
  initialDevice?: string;
}

interface SdpDebugPanelDeviceProps extends SdpDebugPanelBaseProps {
  mode: 'device';
}

type SdpDebugPanelProps = SdpDebugPanelRegieProps | SdpDebugPanelDeviceProps;

// ─── SdpDebugPanel ────────────────────────────────────────────────────────────

export const SdpDebugPanel: React.FC<SdpDebugPanelProps> = (props) => {
  const { log, onClose, onClear } = props;
  const isRegie = props.mode === 'regie';

  const [selectedId,   setSelectedId]   = React.useState<number | null>(log[0]?.id ?? null);
  const [filterDevice, setFilterDevice] = React.useState<string>(
    isRegie && props.mode === 'regie' ? (props.initialDevice ?? 'all') : 'all'
  );
  const [copied, setCopied] = React.useState(false);

  const selected = log.find(e => e.id === selectedId);
  const hasError = log.some(e => e.type === 'error');

  const devices = React.useMemo(() => {
    if (!isRegie) return [];
    const ids = new Set<string>();
    log.forEach(e => { if (e.deviceId) ids.add(e.deviceId); });
    return Array.from(ids);
  }, [log, isRegie]);

  const filtered = React.useMemo(() => {
    if (!isRegie || filterDevice === 'all') return log;
    if (filterDevice === 'whip') {
      return log.filter(e =>
        e.type === 'whip' ||
        e.type === 'whip-offer' ||
        e.type === 'whip-answer' ||
        (!e.deviceId && e.type !== 'event')
      );
    }
    return log.filter(e => e.deviceId === filterDevice || (!e.deviceId && e.type === 'event'));
  }, [log, isRegie, filterDevice]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 5000);
    });
  };

  // ─── Item de liste ──────────────────────────────────────────────────────────

  const renderListItem = (entry: SdpLogEntry) => {
    const isError    = entry.type === 'error';
    const isSent     = entry.direction === 'sent';
    const isInfo     = entry.direction === 'info';
    const isWhip     = entry.type.startsWith('whip');
    const isSelected = entry.id === selectedId;

    const devColor = isRegie && entry.deviceId ? getDeviceColor(entry.deviceId) : '#60a5fa';
    const dotColor = isError ? '#ff3333'
                   : isWhip  ? '#60a5fa'
                   : isInfo  ? '#666666'
                   : isSent  ? '#00ff41'
                   : devColor;

    return (
      <div
        key={entry.id}
        onClick={() => setSelectedId(isSelected ? null : entry.id)}
        style={{
          padding: '7px 10px',
          cursor: 'pointer',
          borderBottom: '1px solid #080f08',
          borderLeft: `3px solid ${isSelected ? dotColor : 'transparent'}`,
          background: isSelected ? '#0a1a0a' : 'transparent',
        }}
      >
        <div style={{ fontSize: 9, color: '#1a4a1a', marginBottom: 2 }}>
          {entry.timestamp}
          {isRegie && entry.deviceId && (
            <span style={{ marginLeft: 6, color: getDeviceColor(entry.deviceId), fontWeight: 700 }}>
              {entry.deviceId}
            </span>
          )}
        </div>

        <div style={{ fontSize: 10, fontWeight: 700, color: dotColor, lineHeight: 1.3 }}>
          {isSent ? '↑' : isInfo ? '•' : '↓'} {entry.label}
        </div>

        {entry.codec?.video && (
          <div style={{ fontSize: 9, color: '#2a6a2a', marginTop: 2 }}>
            {entry.codec.video}
            {entry.codec.profile && (
              <span style={{
                marginLeft: 4,
                color: entry.codec.profile.toLowerCase().startsWith('42e0') ? '#00aa41' : '#aa6600',
              }}>
                {profileLabel(entry.codec.profile)}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  // ─── Panneau de détail ──────────────────────────────────────────────────────

  const renderDetail = () => {
    if (!selected) {
      return (
        <div style={{ color: '#1a4a1a', fontSize: 11, textAlign: 'center', marginTop: 60 }}>
          ← Sélectionnez un événement
        </div>
      );
    }

    const isError = selected.type === 'error';
    const isSent  = selected.direction === 'sent';

    return (
      <>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', marginBottom: 14,
        }}>
          <div>
            <div style={{ color: '#00ff41', fontSize: 15, fontWeight: 700 }}>
              {selected.label}
            </div>
            <div style={{ fontSize: 10, color: '#1a5a1a', marginTop: 2 }}>
              {selected.timestamp}
              {' · '}
              {isSent ? '↑ ENVOYÉ' : !isError ? '↓ REÇU' : '• INFO'}
              {isRegie && selected.deviceId && (
                <span style={{ marginLeft: 8, color: getDeviceColor(selected.deviceId), fontWeight: 700 }}>
                  DEVICE: {selected.deviceId}
                </span>
              )}
            </div>
          </div>

          {selected.sdp && (
            <button
              onClick={() => copyToClipboard(selected.sdp!)}
              style={{
                background: copied ? 'rgba(0,255,65,0.15)' : 'rgba(0,255,65,0.06)',
                border: '1px solid #1a6a1a',
                color: copied ? '#00ff41' : '#1a6a1a',
                fontSize: 10, padding: '4px 12px', borderRadius: 4,
                cursor: 'pointer', letterSpacing: 1,
              }}
            >
              {copied ? '✓ COPIÉ' : 'COPIER SDP'}
            </button>
          )}
        </div>

        {selected.codec && (selected.codec.video || selected.codec.audio) && (
          <div style={{
            marginBottom: 14, padding: '10px 14px',
            background: '#080f08', borderRadius: 6, border: '1px solid #0d2a0d',
          }}>
            <div style={{
              fontSize: 9, color: '#1a6a1a', fontWeight: 700,
              letterSpacing: 2, marginBottom: 8,
            }}>
              CODEC DÉTECTÉ
            </div>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <span style={{ fontSize: 9, color: '#1a5a1a' }}>VIDÉO </span>
                <span style={{ fontSize: 12, color: '#00dd88', fontWeight: 700 }}>
                  {selected.codec.video || '—'}
                </span>
                {selected.codec.profile && (
                  <span style={{
                    fontSize: 10, marginLeft: 6,
                    color: selected.codec.profile.toLowerCase().startsWith('42e0') ? '#00ff41' : '#ffaa00',
                  }}>
                    {profileLabel(selected.codec.profile)}
                  </span>
                )}
              </div>
              <div>
                <span style={{ fontSize: 9, color: '#1a5a1a' }}>AUDIO </span>
                <span style={{ fontSize: 12, color: '#00bb66', fontWeight: 700 }}>
                  {selected.codec.audio || '—'}
                </span>
              </div>
            </div>

            {selected.codec.profile && (
              <div style={{
                marginTop: 8, fontSize: 10, padding: '5px 10px', borderRadius: 4,
                ...(selected.codec.profile.toLowerCase().startsWith('42e0')
                  ? { color: '#00ff41', background: 'rgba(0,255,65,0.05)', border: '1px solid rgba(0,255,65,0.2)' }
                  : { color: '#ffaa00', background: 'rgba(255,170,0,0.06)', border: '1px solid rgba(255,170,0,0.25)' }),
              }}>
                {selected.codec.profile.toLowerCase().startsWith('42e0')
                  ? '✓ Baseline 42e0xx — compatible iOS & Android'
                  : selected.codec.profile.toLowerCase().startsWith('4d')
                    ? '⚠ Main — OK Android, peut bloquer iOS Safari'
                    : selected.codec.profile.toLowerCase().startsWith('64')
                      ? '⚠ High — OK Android/Desktop, INCOMPATIBLE iOS'
                      : '? Profil inconnu'}
              </div>
            )}
          </div>
        )}

        {selected.raw && (
          <details style={{ marginBottom: 12 }}>
            <summary style={{
              cursor: 'pointer', fontSize: 10, color: '#2a5a2a',
              padding: '5px 0', userSelect: 'none', listStyle: 'none',
            }}>
              ▸ MESSAGE BRUT (JSON)
            </summary>
            <pre style={{
              fontSize: 9, color: '#2a7a2a', background: '#030803',
              padding: '8px 10px', borderRadius: 4, overflowX: 'auto',
              whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              border: '1px solid #0a1f0a', margin: '6px 0 0',
              maxHeight: 200, overflowY: 'auto',
            }}>
              {JSON.stringify(selected.raw, null, 2)}
            </pre>
          </details>
        )}

        {selected.sdp ? (
          <div>
            <div style={{
              fontSize: 9, color: '#1a6a1a', fontWeight: 700,
              letterSpacing: 2, marginBottom: 6,
            }}>
              SDP COMPLET ({selected.sdp.length} chars)
            </div>
            <pre style={{
              fontSize: 9, background: '#030a03',
              padding: '10px 12px', borderRadius: 6,
              border: '1px solid #0a1f0a', margin: 0,
              whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              overflowX: 'auto', lineHeight: 1.5,
              maxHeight: 500, overflowY: 'auto',
            }}>
              {selected.sdp.split(/\r?\n/).map((line, i) => colorizeSdpLine(line, i))}
            </pre>
          </div>
        ) : (
          <div style={{ color: '#1a4a1a', fontSize: 10, fontStyle: 'italic' }}>
            (aucun SDP)
          </div>
        )}
      </>
    );
  };

  // ─── Rendu principal ────────────────────────────────────────────────────────

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: '#040a04', display: 'flex', flexDirection: 'column',
      fontFamily: '"Courier New", "Lucida Console", monospace',
    }}>

      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 16px', borderBottom: '1px solid #0d2a0d',
        background: '#020602', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: '#00ff41', display: 'inline-block',
            boxShadow: '0 0 6px #00ff41',
          }} />
          <span style={{ color: '#00ff41', fontWeight: 700, fontSize: 13, letterSpacing: 2 }}>
            SDP DEBUG{isRegie ? ' — RÉGIE' : ''}
          </span>
          <span style={{
            fontSize: 10, color: '#1a5a1a', padding: '2px 6px',
            background: '#0a1a0a', borderRadius: 3, border: '1px solid #0d2a0d',
          }}>
            {log.length} événement{log.length !== 1 ? 's' : ''}
          </span>
          {hasError && (
            <span style={{
              fontSize: 10, color: '#ff3333', padding: '2px 8px',
              background: 'rgba(255,51,51,0.1)', borderRadius: 3,
              border: '1px solid rgba(255,51,51,0.3)',
            }}>
              ⚠ ERREUR
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={onClear}
            style={{
              background: 'transparent', border: '1px solid #0d2a0d',
              color: '#2a5a2a', fontSize: 10, padding: '4px 12px',
              borderRadius: 4, cursor: 'pointer', letterSpacing: 1,
            }}
          >
            EFFACER
          </button>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(0,255,65,0.08)', border: '1px solid #1a6a1a',
              color: '#00ff41', fontSize: 10, padding: '4px 12px',
              borderRadius: 4, cursor: 'pointer', letterSpacing: 1, fontWeight: 700,
            }}
          >
            FERMER
          </button>
        </div>
      </div>

      {/* Filtres device (mode régie uniquement) */}
      {isRegie && (
        <div style={{
          display: 'flex', gap: 6, padding: '6px 12px',
          background: '#020a02', borderBottom: '1px solid #0a1a0a',
          flexShrink: 0, overflowX: 'auto',
        }}>
          {(['all', 'whip', ...devices] as string[]).map(id => {
            const isActive = filterDevice === id;
            const color = id === 'all'  ? '#00ff41'
                        : id === 'whip' ? '#60a5fa'
                        : getDeviceColor(id);
            return (
              <button
                key={id}
                onClick={() => setFilterDevice(id)}
                style={{
                  padding: '3px 10px', borderRadius: 12, fontSize: 9,
                  fontFamily: 'inherit', fontWeight: 700,
                  cursor: 'pointer', letterSpacing: 1, whiteSpace: 'nowrap',
                  border: `1px solid ${isActive ? color : '#0d2a0d'}`,
                  background: isActive ? `${color}18` : 'transparent',
                  color,
                }}
              >
                {id === 'all' ? 'TOUS' : id === 'whip' ? '📡 WHIP' : `📱 ${id}`}
              </button>
            );
          })}
        </div>
      )}

      {/* Corps : liste + détail */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Liste */}
        <div style={{
          width: isRegie ? 220 : 200,
          borderRight: '1px solid #0a1f0a',
          overflowY: 'auto',
          background: '#020802',
          flexShrink: 0,
        }}>
          {filtered.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: '#1a4a1a', fontSize: 10 }}>
              En attente…
            </div>
          )}
          {filtered.map(renderListItem)}
        </div>

        {/* Détail */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#050d05' }}>
          {renderDetail()}
        </div>
      </div>
    </div>
  );
};
